import React, { FC, useState, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from 'components/Card'
import { Flex } from 'components/Flex'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Table, TableHead, TableRow, TableHeadCell, TableBody, TableCell } from 'components/Table'
import { Toggle } from 'components/Toggle'
import { Button } from 'components/Button'
import { LayerPermissions } from 'models/user-permissions'
import { Alert } from 'components/Alert'
import { useUpdatePermissions } from 'services/user'

type LayerPermissionsTableProps = {
  userId: string
  permissions: LayerPermissions[]
}

export const LayerPermissionsTable: FC<LayerPermissionsTableProps> = ({ userId, permissions }) => {
  const { t } = useTranslation()
  const [layers, setLayers] = useState(permissions)
  const [updatePermissions] = useUpdatePermissions(userId)
  const [status, setStatus] = useState('idle')

  const handleLayerChange = (id: number) => {
    const newData = layers.map((layer: LayerPermissions) =>
      layer.id === id ? { ...layer, access: !layer.access } : layer,
    )
    setLayers(newData)
  }

  const handleLayersSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus('loading')

    updatePermissions(
      { layers },
      {
        onError: () => setStatus('error'),
        onSuccess: () => setStatus('success'),
        onSettled: () => {
          setTimeout(() => setStatus('idle'), 1000)
        },
      },
    )
  }

  return (
    <Card>
      <Stack space="large">
        <Stack space="xsmall">
          <Heading size="secondary">{t('user.edit.card.layer.title')}</Heading>
          <Text>{t('user.edit.card.layer.desc')}</Text>
        </Stack>
        <form onSubmit={handleLayersSubmit}>
          <Stack space="medium">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell>
                    <Text weight="strong">{t('user.edit.card.layer.form.name')}</Text>
                  </TableHeadCell>
                  <TableHeadCell>
                    <Text weight="strong">{t('user.edit.card.layer.form.access')}</Text>
                  </TableHeadCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {permissions.map((layer) => (
                  <TableRow key={layer.name}>
                    <TableCell>
                      <Text weight="strong">{layer.name}</Text>
                    </TableCell>
                    <TableCell>
                      <Flex>
                        <Toggle
                          on={layer.access}
                          label={t('user.edit.card.layer.form.access')}
                          onChange={() => handleLayerChange(layer.id)}
                        />
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button type="submit" loading={status === 'loading'}>
              {t('user.edit.save')}
            </Button>

            {status === 'success' && (
              <Alert tone="positive" full>
                {t('common.messages.success')}
              </Alert>
            )}

            {status === 'error' && (
              <Alert tone="critical" full>
                {t('common.messages.error')}
              </Alert>
            )}
          </Stack>
        </form>
      </Stack>
    </Card>
  )
}

LayerPermissionsTable.displayName = 'LayerPermissionsTable'
